import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { isoWeek } from '../../core/calendar';
import { standardLookup, type CourseStandard } from '../../core/course';
import type { AnalyticsEvent, PlannerAction } from '../../core/events';
import { schedule, weekly, type ScheduledUnit, type WeekView, type WeeklyView as Weekly } from '../../core/schedule';
import type { UnitTopic } from '../../core/topics';
import { hasMyWeek } from '../../core/home';
import { courseLabel, type GetToken } from '../api';
import { linkTo, MY_WEEK_HREF, planHref, PLANS_HREF } from '../navigation';
import { usePlan } from '../usePlan';
import { usePlanList } from '../usePlanList';
import { CurrentUnitPicker, shouldAskCurrentUnit, useCurrentUnitAsk } from './CurrentUnitPicker';
import { OverrunPanel } from './OverrunPanel';
import { PlanCalendar, useSelectedUnit } from './PlanCalendar';
import { PlanLoadFailure } from './PlanEditor';
import { TopicList } from './TopicList';
import { unitColor } from '../unit-color';
import { dateRange, day, plural, StandardItem, UnitMeta } from './UnitRow';

/**
 * The weekly view (R13, R15 to R17): the plan's landing page once built. It shows this week and
 * next from the shared `weekly`, as of the date the Worker used (KTD8). "Still on this" adds a
 * week; when that runs the plan over, the teacher chooses what gives (R16). It links to My week
 * whenever `hasMyWeek` offers it (R7, KTD2).
 */
export function WeeklyView({ planId, getToken, onTrack, onOpenTopic, myWeek = false, dayItems = false }: {
  planId: string; getToken: GetToken; onOpenTopic(topic: UnitTopic, unitId: string): void; myWeek?: boolean; dayItems?: boolean;
  onTrack(action: PlannerAction, properties?: AnalyticsEvent['properties']): void;
}) {
  const { data, loadError, retry, busy, error, edit } = usePlan(planId, getToken);
  const [resolving, setResolving] = useState(false);
  const ask = useCurrentUnitAsk();
  const { selectedUnitId, select, container } = useSelectedUnit();
  const trackRef = useRef(onTrack); trackRef.current = onTrack;
  const viewed = useRef(false);
  // The plan list only decides the My week link, so it waits for the plan and never blocks it.
  // No list, or a failed one, is no link: the safe default.
  const [plans] = usePlanList(getToken, data !== null && myWeek);
  const showMyWeek = hasMyWeek((plans ?? []).filter(p => p.built).length, myWeek, dayItems);

  const title = data ? courseLabel(data.plan.course) : null;
  useEffect(() => { if (title) document.title = `This week · ${title} · 240 Planner`; }, [title]);
  // One event per view (KTD12): weekly return is read from its ISO week. My week sends its own (KTD5).
  useEffect(() => {
    if (!data || viewed.current) return;
    viewed.current = true;
    trackRef.current('weekly_viewed', { plan_id: planId, iso_week: isoWeek(data.today), state: weekly(data.plan.plan, data.plan.plan.calendar, data.today).state, surface: 'plan' });
  }, [data, planId]);
  const panelShown = resolving && !!data && data.overrun.units.length > 0;
  const overrunShown = useRef(false);
  useEffect(() => {
    if (panelShown && !overrunShown.current && data) trackRef.current('overrun_shown', { plan_id: planId, weeks_over: data.overrun.weeksOver, units_over: data.overrun.units.length });
    overrunShown.current = panelShown;
  }, [panelShown, data, planId]);

  if (loadError) return <PlanLoadFailure error={loadError} onRetry={retry} />;
  if (!data) return <p className="notice" role="status">Loading your plan…</p>;

  const { plan: record, today, overrun } = data;
  const { plan } = record;
  const rows = schedule(plan, plan.calendar);
  const view = weekly(plan, plan.calendar, today, rows);
  const currentIndex = rows.findIndex(u => u.id === plan.currentUnitId);
  const current = rows[currentIndex];
  const textOf = standardLookup(record.standards);
  const standardsOf = (unitId: string) => plan.standards
    .filter(s => s.placement.place === 'unit' && s.placement.unitId === unitId)
    .map(s => textOf(s.id));
  const unplaced = plan.standards.filter(s => s.placement.place === 'not_yet_placed').length;
  const asking = ask.asking(shouldAskCurrentUnit({ plan, fresh: false, builtAt: record.builtAt, today }));
  // Units after the current one can give weeks back; with no current unit, any not done.
  const later = currentIndex >= 0 ? rows.slice(currentIndex + 1) : rows.filter(u => !u.done);

  const stillOnThis = async () => { const response = await edit({ type: 'still_on_this' }); if (response && response.overrun.units.length > 0) setResolving(true); };
  const indexOf = (unitId: string) => rows.findIndex(u => u.id === unitId);
  const week = (heading: string, w: WeekView | null, shown: ReadonlySet<string> = new Set()) =>
    <WeekSection heading={heading} week={w} currentUnitId={plan.currentUnitId} selectedUnitId={selectedUnitId} indexOf={indexOf} planId={planId} shown={shown} standardsOf={standardsOf} getToken={getToken} onOpenTopic={onOpenTopic} />;
  // A unit picked in the calendar that is not this week or next is named with its dates instead.
  const onScreen = view.state === 'teaching' ? [view.thisWeek, view.nextWeek] : view.state === 'finished' ? [] : [view.nextWeek];
  const picked = rows.find(u => u.id === selectedUnitId);
  const pickedElsewhere = picked && !onScreen.some(w => w?.units.some(u => u.id === picked.id)) ? picked : null;

  return <div className="plan-editor weekly-view has-calendar">
    <div className="page-heading">
      <div>
        <a className="topic-back" {...linkTo(PLANS_HREF)}>← All plans</a>
        {showMyWeek && <a className="topic-back weekly-my-week" {...linkTo(MY_WEEK_HREF)}>My week</a>}
        <h1 className="setup-title">{title}</h1>
        <p className="muted">{current ? <>Your class is on <strong>{current.name}</strong>.</> : 'No current unit set.'}</p>
        {overrun.weeksOver > 0 && !panelShown && <p className="unit-flag" role="status">
          The plan runs {plural(overrun.weeksOver, 'week')} past the last day: {overrun.units.map(u => `${u.name} (${plural(u.weeksOver, 'week')})`).join(', ')}.{' '}
          <button className="text-button" type="button" onClick={() => setResolving(true)}>Shorten or drop a unit</button>
        </p>}
      </div>
      <div className="page-actions">
        {current && view.state !== 'finished' && <button className="primary" type="button" disabled={busy} onClick={() => void stillOnThis()}>Still on this</button>}
        {rows.length > 0 && <button className="secondary" type="button" onClick={ask.open}>{current ? 'Change current unit' : 'Set current unit'}</button>}
        <a className="secondary button-link" {...linkTo(planHref(planId))}>Edit plan</a>
      </div>
    </div>
    <div className="plan-layout">
      <div className="plan-layout-calendar"><PlanCalendar rows={rows} calendar={plan.calendar} today={today} selectedUnitId={selectedUnitId} onSelectUnit={select} /></div>
      <div className="plan-layout-main" ref={container}>
        {error && <p className="error-message" role="alert">{error}</p>}
        <p className="sr-only" role="status">{busy ? 'Saving…' : ''}</p>
        {panelShown && <OverrunPanel overrun={overrun} units={later} busy={busy}
          onShorten={(unitId, by) => void edit({ type: 'shorten', unitId, by })}
          onDrop={unitId => void edit({ type: 'drop_unit', unitId })}
          onLeave={() => setResolving(false)} />}
        {pickedElsewhere && <section className="weekly-selected" aria-label="Selected unit" data-unit-id={pickedElsewhere.id} style={{ '--unit-color': unitColor(indexOf(pickedElsewhere.id)) } as CSSProperties}>
          <p><strong>{pickedElsewhere.name}</strong>: {dateRange(pickedElsewhere)}, {plural(pickedElsewhere.weeks, 'week')}</p>
        </section>}
        <WeekStates view={view} week={week} />
        {unplaced > 0 && <p className="muted weekly-unplaced">{plural(unplaced, 'standard')} {unplaced === 1 ? 'is' : 'are'} not yet placed. <a {...linkTo(planHref(planId))}>Place them in the plan</a></p>}
      </div>
    </div>
    {asking && <CurrentUnitPicker units={rows} currentUnitId={plan.currentUnitId} today={today} busy={busy}
      onClose={ask.close}
      onPick={async unitId => { if (await edit({ type: 'anchor', unitId })) ask.close(); }} />}
  </div>;
}

/** This week and next, a break, the first week, or the finished year (R5 shares it with My week). */
export function WeekStates({ view, week }: { view: Weekly; week(heading: string, w: WeekView | null, shown?: ReadonlySet<string>): ReactNode }) {
  return <>
    {view.state === 'teaching' && <>
      {week('This week', view.thisWeek)}
      {week('Next week', view.nextWeek, new Set(view.thisWeek.units.map(u => u.id)))}
    </>}
    {view.state === 'break' && <>
      <p className="notice weekly-status">{view.breakName} this week. No teaching is planned.</p>
      {week('Next week', view.nextWeek)}
    </>}
    {view.state === 'before_first_day' && <>
      <p className="notice weekly-status">School starts {day(view.firstDay)}.</p>
      {week('First week', view.nextWeek)}
    </>}
    {view.state === 'finished' && <div className="notice weekly-status"><p><strong>The school year is finished.</strong></p><p className="muted">The last day was {day(view.lastDay)}.</p></div>}
  </>;
}

/**
 * One week's units. A unit already shown this week is named, not repeated. My week nests it one
 * heading level down and leaves topics to the plan's own weekly view (Scope Boundaries).
 */
export function WeekSection({ heading, week, currentUnitId, selectedUnitId, indexOf, planId, shown, standardsOf, getToken, onOpenTopic, level = 2 }: {
  heading: string; week: WeekView | null; currentUnitId: string | null; selectedUnitId: string | null; indexOf(unitId: string): number; planId: string; shown: ReadonlySet<string>;
  standardsOf(unitId: string): CourseStandard[]; getToken: GetToken; onOpenTopic?(topic: UnitTopic, unitId: string): void; level?: 2 | 3;
}) {
  const titleId = useId();
  const Heading = level === 2 ? 'h2' : 'h3';
  return <section className="weekly-week" aria-labelledby={titleId}>
    <div className="weekly-week-heading"><Heading id={titleId}>{heading}</Heading>{week && <span className="muted">Week of {day(week.weekStart)}</span>}</div>
    {!week && <p className="muted">There are no more teaching weeks this year.</p>}
    {week && week.units.length === 0 && <p className="muted">Nothing is planned for this week. <a {...linkTo(planHref(planId))}>Add a unit</a></p>}
    {week?.units.map(unit => shown.has(unit.id)
      ? <p key={unit.id} className={`weekly-continues${unit.id === selectedUnitId ? ' selected' : ''}`} data-unit-id={unit.id}><strong>{unit.name}</strong> continues.</p>
      : <WeekUnit key={unit.id} unit={unit} current={unit.id === currentUnitId} selected={unit.id === selectedUnitId} index={indexOf(unit.id)} planId={planId} standards={standardsOf(unit.id)} getToken={getToken} onOpenTopic={onOpenTopic} level={level} />)}
  </section>;
}

function WeekUnit({ unit, current, selected, index, planId, standards, getToken, onOpenTopic, level }: {
  unit: ScheduledUnit; current: boolean; selected: boolean; index: number; planId: string; standards: CourseStandard[]; getToken: GetToken; onOpenTopic?(topic: UnitTopic, unitId: string): void; level: 2 | 3;
}) {
  const titleId = useId();
  const Heading = level === 2 ? 'h3' : 'h4';
  return <article className={`unit-row${current ? ' current' : ''}${unit.pastLastDay ? ' over' : ''}${selected ? ' selected' : ''}`}
    aria-labelledby={titleId} data-unit-id={unit.id} style={{ '--unit-color': unitColor(index) } as CSSProperties}>
    <Heading id={titleId} className="weekly-unit-name">{unit.name}</Heading>
    <UnitMeta unit={unit} current={current} />
    {standards.length
      ? <ul className="plan-standards">{standards.map(s => <StandardItem key={s.id} standard={s} />)}</ul>
      : <p className="muted unit-empty">No standards in this unit yet.</p>}
    {standards.length > 0 && onOpenTopic && <div className="unit-topics">
      <h4 className="weekly-topics-heading">240 topics</h4>
      <TopicList planId={planId} unitId={unit.id} standardIds={standards.map(s => s.id)} getToken={getToken} onOpen={topic => onOpenTopic(topic, unit.id)} />
    </div>}
  </article>;
}
